import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, InputAdornment, Typography, styled,
} from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { showToast } from "@/redux/actions/toast-actions";
import { smsChargeSchema } from "@/schemas/notification.schema";
import { useCreateSmsCharge } from "@/hooks/use-notifications";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 420,
    width: "100%",
  },
}));

const ChargeFormDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const mutation = useCreateSmsCharge();

  const formik = useFormik({
    initialValues: { fee: "" },
    validationSchema: smsChargeSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(
        { fee: Number(values.fee) },
        {
          onSuccess: (res) => {
            dispatch(showToast(res?.message ?? "SMS charge configured", "success"));
            formik.resetForm();
            onClose();
          },
          onError: (err) =>
            dispatch(showToast(err?.message ?? "Failed to configure charge", "error")),
        },
      );
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <Box>
          <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
            Configure SMS Charge
          </DialogTitle>
          {/* The endpoint creates a new charge record rather than editing an
              existing one, so existing charges stay in the list as history. */}
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Creates a new charge. Activate it from the list to start billing.
          </Typography>
        </Box>

        {mutation.isError && (
          <CustomErrorMessage message={mutation.error?.message ?? "Failed to configure charge"} />
        )}

        <FormTextInput
          formik={formik}
          name="fee"
          label="Fee per SMS"
          type="number"
          placeholder="0.00"
          isDisabled={mutation.isPending}
          InputProps={{ startAdornment: <InputAdornment position="start">NGN</InputAdornment> }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Saving..." : "Create Charge"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default ChargeFormDialog;